// ============================================
// ConverseTech Frontend - Gemini Live Hook
// ============================================

import { useState, useRef, useCallback, useEffect } from 'react';
import { GeminiLiveClient, GeminiSessionState } from '../lib/gemini-live';
import type { TrainingMode, GeminiFunctionCall } from '@conversetech/shared';

interface UseGeminiLiveOptions {
  systemPrompt: string;
  mode?: TrainingMode;
  onTranscript?: (role: 'user' | 'assistant', text: string) => void;
  onAudioData?: (data: ArrayBuffer) => void;
  onError?: (error: Error) => void;
  onFunctionCall?: (functionCall: GeminiFunctionCall) => void;
}

export function useGeminiLive({
  systemPrompt,
  mode = 'conversation',
  onTranscript,
  onAudioData,
  onError,
  onFunctionCall,
}: UseGeminiLiveOptions) {
  const [state, setState] = useState<GeminiSessionState>('disconnected');
  const [isConnecting, setIsConnecting] = useState(false);
  const clientRef = useRef<GeminiLiveClient | null>(null);

  const connect = useCallback(async () => {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    if (!apiKey) {
      const error = new Error('Missing VITE_GEMINI_API_KEY environment variable');
      onError?.(error);
      return;
    }

    if (clientRef.current?.isReady) {
      return;
    }

    setIsConnecting(true);

    try {
      clientRef.current = new GeminiLiveClient(
        apiKey,
        systemPrompt,
        {
          onStateChange: setState,
          onTranscript: (role, text) => onTranscript?.(role, text),
          onAudioData: (data) => onAudioData?.(data),
          onError: (error) => onError?.(error),
          onFunctionCall: (fc) => onFunctionCall?.(fc),
        },
        mode
      );

      await clientRef.current.connect();
    } catch (error) {
      console.error('Failed to connect to Gemini:', error);
      onError?.(error instanceof Error ? error : new Error('Connection failed'));
    } finally {
      setIsConnecting(false);
    }
  }, [systemPrompt, mode, onTranscript, onAudioData, onError, onFunctionCall]);

  const disconnect = useCallback(() => {
    clientRef.current?.disconnect();
    clientRef.current = null;
  }, []);

  const sendAudio = useCallback((data: ArrayBuffer) => {
    clientRef.current?.sendAudio(data);
  }, []);

  const sendClientContent = useCallback((text: string) => {
    clientRef.current?.sendClientContent(text);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clientRef.current?.disconnect();
    };
  }, []);

  return {
    state,
    isConnecting,
    isReady: state === 'connected' || state === 'listening',
    connect,
    disconnect,
    sendAudio,
    sendClientContent,
  };
}
